import { Show, ShowFirstMatching } from '@components/Show';
import { flexRender, type Row } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { TableCell } from './TableCell';

interface TableBodyProps<TData = any> {
  rows: Row<TData>[];
  className?: string;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function TableBody<TData = any>({ rows, className, rowVirtualizer }: TableBodyProps<TData>) {
  const anyIsExpanded = rows.some((row) => row.getIsExpanded());

  return (
    <tbody
      className={clsx('bg-white', className)}
      style={{ display: 'grid', height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index] as Row<TData>;

        const isParent = !row.parentId;
        const isChild = !!row.parentId;
        const isSticky = isParent && anyIsExpanded;

        const isLastRow = virtualRow.index === rows.length - 1;
        const isFirstRow = virtualRow.index === 0;

        return (
          <tr
            data-index={virtualRow.index} //needed for dynamic row height measurement
            ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
            key={row.id}
            data-testid={`table-row`}
            data-row-id={row.id}
            data-row-type={isParent ? 'parent' : 'child'}
            className={twMerge(
              'group absolute flex w-full transition-colors duration-150',
              'focus-within:bg-blue-50/75 focus-within:ring-1 focus-within:ring-blue-200 hover:bg-blue-50',
              isParent && 'bg-white',
              isChild && 'bg-gray-50/50 hover:bg-blue-50',
              isSticky && 'z-10 bg-white',
              !isLastRow && 'border-b border-gray-200',
              isFirstRow && 'border-t border-gray-200',
              isLastRow && 'shadow-sm',
            )}
            style={{
              transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
            }}
          >
            {row.getVisibleCells().map((cell) => {
              const isStandaloneDataCell = cell.column.columnDef.meta?.isStandaloneDataCell;

              return (
                <ShowFirstMatching key={cell.id}>
                  <Show when={isStandaloneDataCell}>
                    <td
                      className={clsx('flex px-3 py-4', cell.column.columnDef.meta?.dataCellClassName)}
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  </Show>
                  <Show when={!isStandaloneDataCell}>
                    <TableCell key={cell.id} cell={cell} />
                  </Show>
                </ShowFirstMatching>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
